import { AlertCircle, CheckCircle2, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCount } from "./metrics-drawer.utils";

const SUMMARY_VARIANTS = {
  data: {
    complete: {
      wrapper:
        "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      iconWrapper: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
      Icon: CheckCircle2,
      title: "All data entries complete",
    },
    pending: {
      wrapper:
        "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300",
      iconWrapper: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
      Icon: AlertCircle,
      title: "Pending metrics outstanding",
    },
    empty: {
      wrapper: "border-border/70 bg-card text-foreground",
      iconWrapper: "bg-muted text-muted-foreground",
      Icon: ClipboardCheck,
      title: "No assigned metrics in scope",
    },
    buildDescription: ({ mode, completedCount, missingCount, totalCount }) => {
      if (mode === "empty") {
        return "There are no assigned metrics in the current dashboard scope.";
      }

      if (mode === "complete") {
        return `${formatCount(completedCount)} of ${formatCount(
          totalCount,
        )} assigned metrics have submitted data.`;
      }

      return `There are ${formatCount(
        missingCount,
      )} pending metrics (data not submitted).`;
    },
  },
  target: {
    complete: {
      wrapper:
        "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      iconWrapper: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
      Icon: CheckCircle2,
      title: "All target setups complete",
    },
    pending: {
      wrapper:
        "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300",
      iconWrapper: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
      Icon: AlertCircle,
      title: "Pending metrics outstanding",
    },
    empty: {
      wrapper: "border-border/70 bg-card text-foreground",
      iconWrapper: "bg-muted text-muted-foreground",
      Icon: ClipboardCheck,
      title: "No assigned metrics in scope",
    },
    buildDescription: ({ mode, completedCount, missingCount, totalCount }) => {
      if (mode === "empty") {
        return "There are no assigned metrics in the current dashboard scope.";
      }

      if (mode === "complete") {
        return `${formatCount(completedCount)} of ${formatCount(
          totalCount,
        )} assigned metrics have targets defined.`;
      }

      return `There are ${formatCount(
        missingCount,
      )} pending metrics (targets not defined).`;
    },
  },
};

const MetricsSummaryCard = ({
  completedCount,
  missingCount,
  totalCount,
  variant = "data",
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="rounded-lg border border-border/70 bg-card px-3 py-3 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 animate-pulse items-center justify-center rounded-md bg-muted" />

          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-44 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full max-w-[360px] animate-pulse rounded bg-muted" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  const mode =
    totalCount === 0 ? "empty" : missingCount === 0 ? "complete" : "pending";

  const summaryVariant = SUMMARY_VARIANTS[variant] || SUMMARY_VARIANTS.data;
  const style = summaryVariant[mode];
  const Icon = style.Icon;
  const description = summaryVariant.buildDescription({
    mode,
    completedCount,
    missingCount,
    totalCount,
  });

  return (
    <div className={cn("rounded-lg border px-3 py-3 shadow-sm", style.wrapper)}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
            style.iconWrapper,
          )}
        >
          <Icon className="h-4 w-4" />
        </span>

        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{style.title}</h3>

          <p className="mt-1 text-xs leading-5 opacity-90">{description}</p>
        </div>
      </div>
    </div>
  );
};

export default MetricsSummaryCard;
